import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDeleteModalWindow from 'app/shared-components/confirmDeleteModalWindow';
import handleError from '@fuse/core/errorHandler';
import useMessage from 'src/app/hooks/useMessage';
import {
  useGetAllLeaveCategoryQuery,
  useDeleteLeaveCategoryMutation,
} from 'src/app/services/leaveRequestApi';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import SimpleDialog from '@fuse/core/SimpleDialog';
import AbsenceAsksCategoryForm from './AbsenceAsksCategoryForm';
import FuseLoading from '@fuse/core/FuseLoading';

const AbsenceAsksCategories = () => {
  const { t, i18n } = useTranslation('translations');
  const lang = i18n.language;
  const [currentCategoryId, setCurrentCategoryId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const {
    data: absenceAsksCategories = [],
    refetch,
    isLoading,
  } = useGetAllLeaveCategoryQuery({ lang });
  const [deleteLeaveCategory] = useDeleteLeaveCategoryMutation();
  const { showMessage } = useMessage();

  const showNewItemDialog = () => {
    setCurrentCategoryId(null);
    setIsDialogOpen(true);
  };

  const showEditDialog = (id) => {
    setCurrentCategoryId(id);
    setIsDialogOpen(true);
  };

  const openDeleteConfirm = (id) => {
    setSelectedId(id);
    setIsOpenDeleteModal(true);
  };

  const closeDeleteConfirm = () => {
    setIsOpenDeleteModal(false);
    setSelectedId(null);
  };

  const handleDelete = () => {
    if (selectedId == null) return;
    deleteLeaveCategory({ id: selectedId })
      .unwrap()
      .then(() => {
        showMessage({ message: t('CategorySuccessfullyDeleted'), variant: 'success' });
        closeDeleteConfirm();
        refetch();
      })
      .catch(handleError);
  };

  if (isLoading) return <FuseLoading />;

  return (
    <div style={{ display: 'grid' }}>
      <TableContainer>
        <div className="h-[40px] flex flex-row justify-between items-center dark:bg-dark">
          <h2 className="ml-5">{t('AbsenceAsksCategories')}</h2>
          <Button
            className="mx-8"
            size="small"
            variant="outlined"
            color="secondary"
            onClick={showNewItemDialog}
          >
            <FuseSvgIcon size={20}>react-outline:plus</FuseSvgIcon>
            <span className="mx-8">{t('Add')}</span>
          </Button>
        </div>
        <Table style={{ overflow: 'auto' }}>
          <TableBody className="overflow-y-auto">
            {absenceAsksCategories.map((item) => (
              <TableRow key={item.id} className="bg-white hover:bg-grey-200 dark:bg-dark">
                <TableCell>{item.name}</TableCell>
                <TableCell>
                  <div className="flex">
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      className="mr-5"
                      color="info"
                      onClick={() => showEditDialog(item.id)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      className="mr-10"
                      color="error"
                      onClick={() => openDeleteConfirm(item.id)}
                    >
                      <Delete />
                    </IconButton>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <SimpleDialog
        title={currentCategoryId ? t('EditAbsenceAskCategory') : t('AddAbsenceAskCategory')}
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      >
        <AbsenceAsksCategoryForm
          id={currentCategoryId}
          refetch={refetch}
          onClose={() => setIsDialogOpen(false)}
          isEdit={!!currentCategoryId}
        />
      </SimpleDialog>
      {isOpenDeleteModal && (
        <ConfirmDeleteModalWindow
          onClose={closeDeleteConfirm}
          setIsOpenModal={setIsOpenDeleteModal}
          modalTitle={'AreYouSureToDeleteAbsenceAskCategories'}
          deleteMessage={handleDelete}
          open={isOpenDeleteModal}
        />
      )}
    </div>
  );
};

export default AbsenceAsksCategories;
